// FILE: src/components/admin/DealerPlanPaymentsPanel.jsx
// Shows bidding pass purchases AND listing credit purchases from dealers

import React, { useState, useEffect, useCallback } from 'react';
import adminApi from '../../services/adminAuth';

const fmt = (n) => n ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

const STATUS_STYLE = {
  success:   { bg: '#28a74520', color: '#28a745', border: '#28a74540', label: '✅ Paid'        },
  initiated: { bg: '#17a2b820', color: '#17a2b8', border: '#17a2b840', label: '⏳ In Progress'  },
  pending:   { bg: '#FFD60A20', color: '#856404', border: '#FFD60A40', label: '🕐 Pending'     },
  failed:    { bg: '#dc354520', color: '#dc3545', border: '#dc354540', label: '❌ Failed'       },
};

const PASS_TYPE_LABELS = {
  day:   { label: '1 Day Pass',   icon: '⚡', color: '#d97706' },
  week:  { label: 'Weekly Pass',  icon: '🔥', color: '#7c3aed' },
  month: { label: 'Monthly Pass', icon: '👑', color: '#003566' },
};

const CREDIT_PLAN_LABELS = {
  single:  { label: '1 Credit',   icon: '🏷️', color: '#059669' },
  five:    { label: '5 Credits',  icon: '🏷️', color: '#0057b7' },
  fifteen: { label: '15 Credits', icon: '🏷️', color: '#003566' },
};

export default function DealerPlanPaymentsPanel() {
  const [activeTab, setActiveTab] = useState('passes');

  // Bidding Passes state
  const [passes,      setPasses]      = useState([]);
  const [passLoading, setPassLoading] = useState(true);
  const [passError,   setPassError]   = useState('');

  // Listing Credits state
  const [credits,     setCredits]     = useState([]);
  const [credLoading, setCredLoading] = useState(true);
  const [credError,   setCredError]   = useState('');

  // Shared filter / search
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const loadPasses = useCallback(() => {
    setPassLoading(true);
    setPassError('');
    adminApi.get('/admin/dealer_plan_payments/bidding_passes')
      .then(res  => setPasses(res.data?.data || []))
      .catch(err => setPassError(err.response?.data?.error || 'Failed to load bidding passes'))
      .finally(  () => setPassLoading(false));
  }, []);

  const loadCredits = useCallback(() => {
    setCredLoading(true);
    setCredError('');
    adminApi.get('/admin/dealer_plan_payments/listing_credits')
      .then(res  => setCredits(res.data?.data || []))
      .catch(err => setCredError(err.response?.data?.error || 'Failed to load listing credits'))
      .finally(  () => setCredLoading(false));
  }, []);

  useEffect(() => { loadPasses(); loadCredits(); }, [loadPasses, loadCredits]);

  // ── Summary stats ──────────────────────────────────────────────────────────
  const passStats = {
    total:   passes.length,
    paid:    passes.filter(p => p.status === 'success').length,
    pending: passes.filter(p => ['pending', 'initiated'].includes(p.status)).length,
    revenue: passes.filter(p => p.status === 'success').reduce((s, p) => s + (p.amount || 0), 0),
  };
  const credStats = {
    total:         credits.length,
    paid:          credits.filter(c => c.status === 'success').length,
    pending:       credits.filter(c => ['pending', 'initiated'].includes(c.status)).length,
    revenue:       credits.filter(c => c.status === 'success').reduce((s, c) => s + (c.amount || 0), 0),
    creditsIssued: credits.filter(c => c.status === 'success').reduce((s, c) => s + (c.credits_granted || 0), 0),
  };

  // ── Filter helpers ─────────────────────────────────────────────────────────
  const filterData = (data) => {
    const q = search.toLowerCase();
    return data.filter(row => {
      const matchSearch =
        String(row.id).includes(q) ||
        (row.dealer_name  || '').toLowerCase().includes(q) ||
        (row.dealer_email || '').toLowerCase().includes(q) ||
        (row.dealer_phone || '').includes(q);
      const matchFilter = filter === 'all' || row.status === filter;
      return matchSearch && matchFilter;
    });
  };

  const STATUS_FILTERS = ['all', 'success', 'initiated', 'pending', 'failed'];

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  const fmtDateTime = (iso) => iso
    ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '—';

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>

      {/* Page heading */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#003566' }}>
          💳 Dealer Plan Payments
        </h2>
        <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
          Track bidding pass purchases and listing credit purchases by dealers
        </p>
      </div>

      {/* ── Sub-tab switcher ── */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 20,
        background: '#fff', padding: 6, borderRadius: 10,
        border: '1px solid #e0e0e0', width: 'fit-content',
      }}>
        {[
          { key: 'passes',  label: '🔨 Bidding Passes',  count: passes.length  },
          { key: 'credits', label: '🏷️ Listing Credits', count: credits.length },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => { setActiveTab(t.key); setSearch(''); setFilter('all'); }}
            style={{
              padding: '9px 20px', borderRadius: 8, fontWeight: 700,
              fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
              background: activeTab === t.key
                ? 'linear-gradient(135deg,#003566,#001d3d)'
                : 'transparent',
              color:  activeTab === t.key ? '#FFD60A' : '#666',
              border: activeTab === t.key ? '1.5px solid #FFD60A' : '1.5px solid transparent',
            }}
          >
            {t.label}
            <span style={{
              marginLeft: 6, fontSize: 11, fontWeight: 800,
              padding: '1px 7px', borderRadius: 20,
              background: activeTab === t.key ? 'rgba(255,214,10,0.2)' : '#f0f0f0',
              color:      activeTab === t.key ? '#FFD60A' : '#888',
            }}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {/* ══════════ BIDDING PASSES ══════════ */}
      {activeTab === 'passes' && (
        <>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20 }}>
            {[
              { label: 'Total Purchases', value: passStats.total,         color: '#003566' },
              { label: 'Paid / Active',   value: passStats.paid,          color: '#28a745' },
              { label: 'Pending',         value: passStats.pending,       color: '#d97706' },
              { label: 'Revenue',         value: fmt(passStats.revenue),  color: '#7c3aed' },
            ].map(s => (
              <div key={s.label} style={{
                background: '#fff', borderRadius: 10,
                padding: '14px 18px', border: '1px solid #e0e0e0',
              }}>
                <p style={{ margin: '0 0 4px', fontSize: 10, color: '#999', fontWeight: 700, textTransform: 'uppercase' }}>
                  {s.label}
                </p>
                <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Search + Filter */}
          <SearchFilter
            search={search} setSearch={setSearch}
            filter={filter} setFilter={setFilter}
            filters={STATUS_FILTERS} data={passes} onRefresh={loadPasses}
          />

          {/* Table */}
          {passLoading ? <Loader /> : passError ? <ErrorBox msg={passError} /> : (
            <TableWrapper>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#003566' }}>
                    {['#ID', 'Dealer', 'Phone', 'Pass Type', 'Duration', 'Amount', 'Status', 'Expires', 'Date'].map(h => (
                      <TH key={h}>{h}</TH>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filterData(passes).length === 0 ? (
                    <EmptyRow cols={9} msg={passes.length === 0 ? 'No bidding pass purchases yet' : 'No results match your search'} />
                  ) : filterData(passes).map((p, i) => {
                    const ss = STATUS_STYLE[p.status] || STATUS_STYLE.pending;
                    const pt = PASS_TYPE_LABELS[p.pass_type] || { label: p.pass_type, icon: '🎫', color: '#666' };
                    return (
                      <tr
                        key={p.id}
                        style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                        onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}
                      >
                        <TD><strong style={{ color: '#003566' }}>#{p.id}</strong></TD>
                        <TD>
                          <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: 13, color: '#0a0a0a' }}>{p.dealer_name || '—'}</p>
                          <p style={{ margin: 0, fontSize: 11, color: '#999' }}>{p.dealer_email}</p>
                        </TD>
                        <TD>{p.dealer_phone ? `+91 ${p.dealer_phone}` : '—'}</TD>
                        <TD>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: `${pt.color}15`, color: pt.color,
                            border: `1px solid ${pt.color}40`,
                            padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                          }}>
                            {pt.icon} {pt.label}
                          </span>
                        </TD>
                        <TD>{p.duration_days ? `${p.duration_days} day${p.duration_days > 1 ? 's' : ''}` : '—'}</TD>
                        <TD>
                          <strong style={{ color: p.status === 'success' ? '#28a745' : '#333' }}>
                            {fmt(p.amount)}
                          </strong>
                        </TD>
                        <TD>
                          <span style={{
                            padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                            background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`,
                            whiteSpace: 'nowrap',
                          }}>
                            {ss.label}
                          </span>
                        </TD>
                        <TD style={{
                          fontSize: 12,
                          color: p.expires_at && new Date(p.expires_at) < new Date() ? '#dc3545' : '#166534',
                        }}>
                          {p.expires_at ? fmtDateTime(p.expires_at) : '—'}
                        </TD>
                        <TD style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                          {fmtDate(p.created_at)}
                        </TD>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableWrapper>
          )}
        </>
      )}

      {/* ══════════ LISTING CREDITS ══════════ */}
      {activeTab === 'credits' && (
        <>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 20 }}>
            {[
              { label: 'Total Purchases', value: credStats.total,          color: '#003566' },
              { label: 'Paid',            value: credStats.paid,           color: '#28a745' },
              { label: 'Pending',         value: credStats.pending,        color: '#d97706' },
              { label: 'Revenue',         value: fmt(credStats.revenue),   color: '#7c3aed' },
              { label: 'Credits Issued',  value: credStats.creditsIssued,  color: '#0057b7' },
            ].map(s => (
              <div key={s.label} style={{
                background: '#fff', borderRadius: 10,
                padding: '14px 18px', border: '1px solid #e0e0e0',
              }}>
                <p style={{ margin: '0 0 4px', fontSize: 10, color: '#999', fontWeight: 700, textTransform: 'uppercase' }}>
                  {s.label}
                </p>
                <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Search + Filter */}
          <SearchFilter
            search={search} setSearch={setSearch}
            filter={filter} setFilter={setFilter}
            filters={STATUS_FILTERS} data={credits} onRefresh={loadCredits}
          />

          {/* Table */}
          {credLoading ? <Loader /> : credError ? <ErrorBox msg={credError} /> : (
            <TableWrapper>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#003566' }}>
                    {['#ID', 'Dealer', 'Phone', 'Plan', 'Credits', 'Amount', 'Status', 'Date'].map(h => (
                      <TH key={h}>{h}</TH>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filterData(credits).length === 0 ? (
                    <EmptyRow cols={8} msg={credits.length === 0 ? 'No listing credit purchases yet' : 'No results match your search'} />
                  ) : filterData(credits).map((c, i) => {
                    const ss = STATUS_STYLE[c.status] || STATUS_STYLE.pending;
                    const cp = CREDIT_PLAN_LABELS[c.plan_type] || { label: c.plan_type, icon: '🏷️', color: '#003566' };
                    return (
                      <tr
                        key={c.id}
                        style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                        onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}
                      >
                        <TD><strong style={{ color: '#003566' }}>#{c.id}</strong></TD>
                        <TD>
                          <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: 13, color: '#0a0a0a' }}>{c.dealer_name || '—'}</p>
                          <p style={{ margin: 0, fontSize: 11, color: '#999' }}>{c.dealer_email}</p>
                        </TD>
                        <TD>{c.dealer_phone ? `+91 ${c.dealer_phone}` : '—'}</TD>
                        <TD>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: `${cp.color}15`, color: cp.color,
                            border: `1px solid ${cp.color}40`,
                            padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                          }}>
                            {cp.icon} {cp.label}
                          </span>
                        </TD>
                        <TD>
                          <span style={{
                            background: '#003566', color: '#FFD60A',
                            fontWeight: 800, fontSize: 14,
                            padding: '3px 12px', borderRadius: 20,
                          }}>
                            {c.credits_granted || 0}
                          </span>
                        </TD>
                        <TD>
                          <strong style={{ color: c.status === 'success' ? '#28a745' : '#333' }}>
                            {fmt(c.amount)}
                          </strong>
                        </TD>
                        <TD>
                          <span style={{
                            padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                            background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`,
                            whiteSpace: 'nowrap',
                          }}>
                            {ss.label}
                          </span>
                        </TD>
                        <TD style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                          {fmtDate(c.created_at)}
                        </TD>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableWrapper>
          )}
        </>
      )}
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function SearchFilter({ search, setSearch, filter, setFilter, filters, data, onRefresh }) {
  const counts = filters.reduce((acc, f) => {
    acc[f] = f === 'all' ? data.length : data.filter(r => r.status === f).length;
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
      {/* Search input */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#fff', border: '1px solid #e0e0e0',
        borderRadius: 8, padding: '8px 14px', flex: 1, minWidth: 200,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="8" stroke="#999" strokeWidth="2"/>
          <path d="M21 21L16.65 16.65" stroke="#999" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <input
          placeholder="Search by dealer name, email, phone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ border: 'none', outline: 'none', fontSize: 13, flex: 1, color: '#333' }}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: 16 }}
          >
            ×
          </button>
        )}
      </div>

      {/* Status filter buttons */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '7px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700,
              cursor: 'pointer', transition: 'all 0.15s',
              background: filter === f ? '#003566' : '#f5f5f5',
              color:      filter === f ? '#FFD60A' : '#555',
              border:     filter === f ? '1px solid #FFD60A' : '1px solid #ddd',
            }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            <span style={{
              marginLeft: 5, fontSize: 10, fontWeight: 800,
              padding: '1px 6px', borderRadius: 20,
              background: filter === f ? '#FFD60A' : '#ddd',
              color:      filter === f ? '#003566' : '#555',
            }}>
              {counts[f] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Refresh button */}
      <button
        onClick={onRefresh}
        style={{
          padding: '8px 14px', background: '#fff',
          border: '1px solid #003566', borderRadius: 7,
          color: '#003566', fontWeight: 700, fontSize: 12, cursor: 'pointer',
        }}
      >
        🔄 Refresh
      </button>
    </div>
  );
}

const TH = ({ children }) => (
  <th style={{
    padding: '11px 12px', textAlign: 'left', fontSize: 10,
    fontWeight: 700, color: '#FFD60A', textTransform: 'uppercase',
    letterSpacing: 0.5, whiteSpace: 'nowrap',
  }}>
    {children}
  </th>
);

const TD = ({ children, style = {} }) => (
  <td style={{ padding: '12px 12px', verticalAlign: 'middle', ...style }}>
    {children}
  </td>
);

const TableWrapper = ({ children }) => (
  <div style={{
    background: '#fff', borderRadius: 12,
    border: '1px solid #e0e0e0', overflow: 'hidden', overflowX: 'auto',
  }}>
    {children}
  </div>
);

const EmptyRow = ({ cols, msg }) => (
  <tr>
    <td colSpan={cols} style={{ padding: '48px 20px', textAlign: 'center', color: '#999' }}>
      <p style={{ fontSize: 28, margin: '0 0 8px' }}>💳</p>
      <p style={{ margin: 0, fontSize: 14, color: '#555' }}>{msg}</p>
    </td>
  </tr>
);

const Loader = () => (
  <div style={{ textAlign: 'center', padding: '48px 0' }}>
    <div style={{
      width: 36, height: 36,
      border: '4px solid #e0e0e0', borderTopColor: '#003566',
      borderRadius: '50%', animation: 'spin 0.8s linear infinite',
      margin: '0 auto 12px',
    }} />
    <p style={{ color: '#666', margin: 0 }}>Loading…</p>
  </div>
);

const ErrorBox = ({ msg }) => (
  <div style={{
    background: '#dc354510', border: '1px solid #dc3545',
    borderRadius: 8, padding: '12px 16px', color: '#dc3545', fontSize: 13,
  }}>
    ⚠️ {msg}
  </div>
);